/**
 * Organizations search page, can be accessed by going to /organizations.
 */
import { GetServerSideProps, NextPage } from 'next';
import initApollo, { addApolloState } from '~/jobiqo/apollo/initApollo';
import OrganizationSearch from '~/jobiqo/components/OrganizationSearch';
import { getQueryVariables } from '~/jobiqo/components/Search/utils/searchHelpers';
import { getGSSPPageData } from '~/jobiqo/lib/data/getPageData';
import { commonQueries } from '~/jobiqo/lib/data/commonQueries';
import {
  getSearchQueryStringObject,
  getSearchRoute,
  checkUnknownRoute,
  removeRouteInfoFromURL,
} from '~/jobiqo/lib/search';
import GET_ORGANIZATIONS from '~/jobiqo/pages/organizations/graphql/organizationProfileSearch.gql';
import { FacetConfig } from '~/jobiqo/models/search';
import { getOriginalLink } from '~/jobiqo/utils/links';
import {
  addSearchUrlItem,
  clearSearchUrlItems,
  URLfilter,
} from '~/jobiqo/services/searchUrlItems';
import { useEffect } from 'react';
import { isRedirectCode, isPermanentRedirectCode } from '~/jobiqo/lib/redirect';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import { getAppConfig, getServerConfigByName } from '~/jobiqo/lib/config';

interface OrganizationsSearchPageProps {
  querystring: any;
  loading: boolean;
  error: boolean;
  data: any;
  urlItemsToAdd: URLfilter[];
}

const OrganizationsSearchPage: NextPage<OrganizationsSearchPageProps> = ({
  querystring,
  loading,
  error,
  data,
  urlItemsToAdd,
}) => {
  useEffect(() => {
    // Start by clearing old items and re-adding the new ones.
    clearSearchUrlItems();
    // Add the items to the clean url.
    urlItemsToAdd.forEach((item) => {
      addSearchUrlItem(item);
    });
  }, [urlItemsToAdd]);
  return (
    <OrganizationSearch
      querystring={querystring}
      loading={loading}
      error={error}
      data={data}
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const frontendConfig = getServerFrontendConfig();
  const organizationsSearchFacetsConfig: FacetConfig[] =
    frontendConfig.ORGANIZATIONS.FACETS;
  const ORGANIZATIONS_SEARCH_LISTING_NUMBER =
    frontendConfig.ORGANIZATIONS.ORGANIZATIONS_SEARCH_LISTING_NUMBER;
  const CLEAN_FACETS_ORDER =
    frontendConfig.ORGANIZATIONS.SEO.CLEAN_FACETS_ORDER;
  const pageData = await getGSSPPageData(ctx);
  const backendConfig = await getAppConfig(ctx);
  const SITE_CAMPAIGN_ENABLED: boolean = !!getServerConfigByName(
    backendConfig,
    'site_campaign_enabled',
  )?.value[0];

  const { query, locale } = ctx;
  // Returns information about search data,
  // including clean url data.
  const route: any = await getSearchRoute(ctx);

  if (isRedirectCode(route?.code) && ctx.resolvedUrl !== route?.target) {
    return {
      redirect: {
        destination: route.target,
        permanent: isPermanentRedirectCode(route?.code),
      },
      props: {},
    };
  }

  const { urlItemsToAdd, newQueryString } = getSearchQueryStringObject(
    query,
    route,
    ctx,
  );

  // we need to remove route from the resolvedUrl and this will be "path".
  const cleanedPath = removeRouteInfoFromURL(ctx.resolvedUrl, route, query);
  // Since the page could be translated we need to pass the backend the
  // url the backend knows.
  const pageActualURL = getOriginalLink(cleanedPath, ctx.locale);

  const notFound = checkUnknownRoute(
    route,
    query,
    newQueryString,
    CLEAN_FACETS_ORDER,
  );

  if (notFound) {
    return {
      notFound: true,
    };
  }

  const apolloClient = initApollo({}, ctx);

  await commonQueries(ctx, apolloClient, cleanedPath, {
    siteCampaignEnabled: SITE_CAMPAIGN_ENABLED,
  });

  const { error, data, loading } = await apolloClient.query({
    query: GET_ORGANIZATIONS,
    variables: {
      ...getQueryVariables(
        newQueryString,
        ORGANIZATIONS_SEARCH_LISTING_NUMBER,
        organizationsSearchFacetsConfig,
        locale,
      ),
      searchPath: pageActualURL,
    },
  });

  return addApolloState(apolloClient, {
    props: {
      ...pageData,
      path: cleanedPath,
      querystring: newQueryString,
      loading,
      error: error ? true : false,
      data,
      urlItemsToAdd,
    },
  });
};

export default OrganizationsSearchPage;
